import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AllPrograms } from "../interfaces/Program";

interface ProgramCardProps {
  program: AllPrograms;
}

export default function ProgramCard({ program }: ProgramCardProps) {
  const { t, i18n } = useTranslation();
  const startPos = useRef<{ x: number; y: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    startPos.current = { x: e.clientX, y: e.clientY };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length > 0) {
      startPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleClickCapture = (e: React.MouseEvent) => {
    if (startPos.current) {
      const deltaX = Math.abs(e.clientX - startPos.current.x);
      const deltaY = Math.abs(e.clientY - startPos.current.y);
      // If user dragged more than 6px, prevent opening the link
      if (deltaX > 6 || deltaY > 6) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  const locale = i18n.language === "fr" ? "fr-FR" : "en-US";
  const formattedDate = new Intl.DateTimeFormat(locale, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(program.startDate));

  return (
    <Link
      to={`/program/${program.slug}`}
      draggable={false}
      onDragStart={(e) => e.preventDefault()}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onClickCapture={handleClickCapture}
      className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col h-full border border-gray-100 block select-none cursor-pointer"
    >
      <div className="w-full h-44 sm:h-48 bg-gray-100 overflow-hidden relative select-none">
        <img
          src={program.icon}
          alt={program.title}
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 pointer-events-none select-none"
        />
      </div>
      <div className="p-3 sm:p-3.5 flex-1 flex flex-col justify-between select-none">
        <div>
          <div className="flex justify-between items-start gap-2 mb-2 min-h-[2.5rem]">
            <h3
              className="text-sm sm:text-base font-semibold text-[#263238] group-hover:text-[#037897] transition-colors leading-snug line-clamp-2 flex-1"
              title={program.title}
            >
              {program.title}
            </h3>
            <span className="text-[#037897] font-bold text-sm sm:text-base whitespace-nowrap pt-0.5">
              ${program.cost}
            </span>
          </div>
          <div className="flex justify-between items-start text-[11px] sm:text-xs mb-3 pt-2 border-t border-gray-100 gap-2">
            <div className="min-w-0">
              <span className="text-[#607D8B] block mb-0.5">{t("programs.date")}</span>
              <p className="font-semibold text-[#263238]">{formattedDate}</p>
            </div>
            <div className="text-right min-w-0">
              <span className="text-[#607D8B] block mb-0.5 truncate">
                {t("programs.slotsReserved")}
              </span>
              <p className="font-semibold text-[#263238]">
                {program.maxParticipants < 9999
                  ? program.bookedSlots
                    ? `${program.bookedSlots} / ${program.maxParticipants}`
                    : `0 / ${program.maxParticipants}`
                  : t("programs.noLimits")}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-auto pt-1">
          <span className="w-full uppercase bg-[#037897] group-hover:bg-[#02627c] text-white py-2 px-3 font-semibold rounded-md text-center text-xs sm:text-sm transition-colors duration-200 block shadow-sm">
            {t("programs.bookNow")}
          </span>
        </div>
      </div>
    </Link>
  );
}
